import React, { useEffect, useRef } from "react";
import Layout from "../../components/layout/layout";
import {
  ChartLineIcon,
  FlashIcon,
  UsersTreeIcon,
  WifiHighIcon,
} from "../../assets/images/svgExports";
import CareersHeroImage from "../../assets/images/careers-hero.png";
import CurrentOpeningImage from "../../assets/images/Calendar.png";

import gsap from "gsap";

type WhyWorkHereCard = {
  icon: React.ComponentType<{ color?: string }>;
  title: string;
  description: string;
};
const whyWorkHereCard: WhyWorkHereCard[] = [
  {
    icon: FlashIcon,
    title: "Fast-Paced Innovation",
    description:
      "We blend technology with entertainment to create industry-first campaigns.",
  },
  {
    icon: ChartLineIcon,
    title: "Room To Grow",
    description:
      "We invest in our people, providing professional development and leadership opportunities.",
  },
  {
    icon: WifiHighIcon,
    title: "Telecom & VAS Expertise",
    description:
      "You'll gain insider knowledge of Africa's rapidly evolving telecom landscape.",
  },
  {
    icon: UsersTreeIcon,
    title: "Collaborative Culture",
    description:
      "At Bridgetune, every voice matters. We embrace diverse perspectives to spark fresh ideas.",
  },
];

const CompanyPage = () => {
  const careersRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.7,
        },
      });

      heroTl
        .from(".careers-hero-card", {
          y: 20,
          opacity: 0,
        })
        .from(
          ".careers-hero-image",
          {
            y: 20,
            opacity: 0,
          },
          "-=0.4"
        );

      gsap.from(".careers-why-card", {
        y: 20,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.08,
        delay: 0.15,
      });

      gsap.from(".careers-openings", {
        y: 24,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        delay: 0.2,
      });

      gsap.from(".careers-cta", {
        y: 24,
        opacity: 0,
        duration: 0.75,
        ease: "power3.out",
        delay: 0.25,
      });
    }, careersRef);

    return () => ctx.revert();
  }, []);

  return (
    <Layout
      footerData={{
        headerText: "Don't See The Right Role?",
        bodyText:
          " We're always interested in meeting talented people. Send us your resume and tell us how you'd like to contribute to Bridgetune's mission.",
        buttonText: " Send Your Resume",
      }}
    >
      <div ref={careersRef} className="h-full z-9 md:mt-0 md:pt-24 bg-white">
        <div className="max-w-6xl mx-auto md:px-6 py-12 md:py-20">
          <section className="flex flex-col-reverse md:flex-row items-center mb-16 md:mb-24">
            <div className="careers-hero-card w-[90%] lg:w-[500px] bg-[#F0F6FF] rounded-b-[40px] lg:rounded-r-[0] md:rounded-l-[60px] p-6 sm:p-8 lg:p-10 flex flex-col items-center md:items-start">
              <button
                className="
                      inline-flex items-center mb-6 justify-center
                      px-4 py-2
                      rounded-full
                      bg-[#C4DFFB]
                      text-[#2C73DB]
                      border border-white/80
                      text-xs
                      font-[300]
                      shadow-[0_0_18px_rgba(59,130,246,0.25)]
                      transition-shadow
                    "
              >
                Join Our Team
              </button>

              <h1 className="text-3xl md:text-4xl font-bold text-[#1e1e1e] mb-4 leading-tight text-center md:text-start">
                Join Our Team Of
                <br />
                <span>Innovators</span>
              </h1>
              <p className="text-[#8e8e8e] text-sm  md:text-md  max-w-[90%] text-center md:text-start">
                We're always on the lookout for curious minds, creative
                thinkers, and passionate go-getters. If you're ready to redefine
                tech-entertainment in Africa—and join a licensed VAS leader—come
                be part of our team!
              </p>
            </div>

            <div className="careers-hero-image w-full lg:w-[calc(100%-500px)]">
              <div className="rounded-3xl overflow-hidden w-full">
                <img
                  src={CareersHeroImage}
                  alt="Team collaborating in the office"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </section>

          {/* WHY WORK HERE */}
          <section className="mb-16 md:mb-20  px-6 md:px-0">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-2">
                Why Work Here?
              </h2>
              <p className="text-[#8e8e8e] text-sm md:text-base max-w-xl mx-auto">
                Discover what makes Bridgetune an exciting place to build your
                career.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {whyWorkHereCard.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="careers-why-card  bg-[#F0F6FF] rounded-3xl px-5 py-6 text-center shadow-sm"
                  >
                    <div className="w-10 h-10 mt-8 mx-auto mb-4 rounded-2xl flex items-center justify-center text-blue-600 text-xl">
                      {/* SVG icon instead of text */}
                      <Icon />
                    </div>
                    <p className="text-md mt-10 md:text-xl font-bold text-[#1e1e1e] mb-2">
                      {card.title}
                    </p>
                    <p className="text-xs md:text-sm text-[#8e8e8e]">
                      {card.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CURRENT OPENINGS */}
          <section className="careers-openings bg-blue-50 rounded-3xl px-6 py-10 md:px-14 md:py-14 text-center shadow-sm">
            <h2 className="text-xl md:text-2xl font-semibold text-neutral-900 mb-2">
              Current Openings
            </h2>
            <p className="text-[#8e8e8e] text-sm md:text-base mb-8">
              No current open roles
            </p>

            <div className="mb-4">
              <div className="w-60 h-32 mx-auto mb-12 justify-center">
                <img
                  src={CurrentOpeningImage}
                  alt="Team collaborating in the office"
                  className="w-full  object-cover"
                />
              </div>
              <h3 className="text-lg font-semibold text-[#1e1e1e] mb-2">
                Check Back Soon For More Roles
              </h3>
              <p className="text-xs text-center w-full md:text-sm text-[#8e8e8e]">
                We're always growing our team. Follow us on social media or
                check back regularly for new opportunities.
              </p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default CompanyPage;
